import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RailGroupActionButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroupActionButton";
import { cn } from "@/utils";
import {
  Box,
  File as FileIcon,
  FolderTree,
  Globe,
  Layers,
  PlusCircle,
  SlidersHorizontal,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  anchorCenterFromElement,
  dispatchCanvasToggleAddContext,
} from "./canvasRailAddContextEvents";
import type { ContentsViewMode } from "./canvasRailContentsModel";

interface CanvasRailContentsControlsProps {
  canvasId: string;
  canEdit: boolean;
  optionalFilters: Array<"entity" | "object">;
  onToggleOptionalFilter: (cat: "entity" | "object") => void;
  viewMode: ContentsViewMode;
  onChangeViewMode: (next: ContentsViewMode) => void;
}

/** Filter + view controls for the Canvas Contents rail section header. */
export function CanvasRailContentsControls({
  canvasId,
  canEdit,
  optionalFilters,
  onToggleOptionalFilter,
  viewMode,
  onChangeViewMode,
}: CanvasRailContentsControlsProps) {
  const [isAddContextOpen, setIsAddContextOpen] = useState(false);

  useEffect(() => {
    const onState = (e: Event) => {
      const { canvasId: cid, open } = (e as CustomEvent).detail as {
        canvasId?: string;
        open: boolean;
      };
      if (cid && cid !== canvasId) return;
      setIsAddContextOpen(open);
    };
    window.addEventListener("canvas:add-context-state", onState);
    return () =>
      window.removeEventListener("canvas:add-context-state", onState);
  }, [canvasId]);

  const handleToggleAddContext = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      dispatchCanvasToggleAddContext(
        canvasId,
        anchorCenterFromElement(e.currentTarget),
        { anchorToRailTrigger: true },
      );
    },
    [canvasId],
  );

  const showEntities = optionalFilters.includes("entity");
  const showObjects = optionalFilters.includes("object");

  return (
    <div className="flex items-center gap-0.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="View options"
            aria-label="View options"
            className={cn(
              "inline-flex size-7 items-center justify-center rounded-md text-n-50",
              "hover:bg-n-10 hover:text-n-80",
              "data-[state=open]:bg-n-10 data-[state=open]:text-n-80",
            )}
          >
            <SlidersHorizontal className="size-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Show
          </DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked
            disabled
            onSelect={(e) => e.preventDefault()}
            className="gap-2"
          >
            <FileIcon className="h-3.5 w-3.5 text-muted-foreground" />
            Files
            <span className="ml-auto text-[10px] text-muted-foreground/60">
              Always
            </span>
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={showEntities}
            onSelect={(e) => {
              e.preventDefault();
              onToggleOptionalFilter("entity");
            }}
            className="gap-2"
          >
            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
            Entities
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={showObjects}
            onSelect={(e) => {
              e.preventDefault();
              onToggleOptionalFilter("object");
            }}
            className="gap-2"
          >
            <Box className="h-3.5 w-3.5 text-muted-foreground" />
            Objects
          </DropdownMenuCheckboxItem>

          <DropdownMenuSeparator />

          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Group by
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={viewMode}
            onValueChange={(v) => onChangeViewMode(v as ContentsViewMode)}
          >
            <DropdownMenuRadioItem value="section" className="gap-2">
              <FolderTree className="h-3.5 w-3.5 text-muted-foreground" />
              Section
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="platform" className="gap-2">
              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
              Type
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {canEdit ? (
        <RailGroupActionButton
          data-canvas-add-context-trigger
          label={isAddContextOpen ? "Close add context" : "Add context"}
          icon={PlusCircle}
          onClick={handleToggleAddContext}
        />
      ) : null}
    </div>
  );
}
